<template>
    <v-container>
        <!-- :picker-date.sync="showPickerDate" -->
        <!-- <v-date-picker
             v-model="pickerDate"
             full-width
             ></v-date-picker> -->

        <v-data-table
            :headers="headers"
            :items="readings"
            :items-per-page="24" 
            :footer-props="{
                'items-per-page-options' : [4 * 6, 8 * 6, 12 * 6, -1]
            }"
            dense
            class="elevation-5"
        >

            <template v-slot:top>
                <v-toolbar flat>
                    <v-toolbar-title>
                        {{ accountTitle }}
                    </v-toolbar-title>

                    <v-spacer></v-spacer>

                    <v-btn icon @click="showPreviousDate">
                        <v-icon>mdi-chevron-left</v-icon>
                    </v-btn>

                    <v-btn icon @click="showToday">
                        <v-icon>mdi-calendar-today</v-icon>
                    </v-btn>

                    <v-toolbar-title>
                        {{ localDate }}
                    </v-toolbar-title>

                    <v-btn icon @click="showNextDate">
                        <v-icon>mdi-chevron-right</v-icon>
                    </v-btn>

                    <v-spacer></v-spacer>

                    <v-btn icon @click="downloadCsv">
                        <v-icon>mdi-download-circle-outline</v-icon>
                    </v-btn>
                </v-toolbar>
            </template>

        </v-data-table>
    </v-container>
</template>

<script>
import { API, Hub } from 'aws-amplify';
import { listSensorDayReadingsByAccountByDate } from '@/graphql/queries';
import { compareSensorsByName, pingAccount, peek } from '@/tools';
peek( 'lint' );

const dayjs = require( 'dayjs' );
const utcPlugin = require( 'dayjs/plugin/utc' );
dayjs.extend( utcPlugin );

const Papa = require( 'papaparse' );

function createSensorDayReadingHeaders( sensorDayReading ) {
    const hasChannel2 = sensorDayReading.channel2Name != null;
    const channel1Header = {
        name : sensorDayReading.name,
        header : {
            text : sensorDayReading.name,
            value : sensorDayReading.serial,
        }
    };
    const channel2Header = ! hasChannel2 ? {} : {
        name : sensorDayReading.channel2Name,
        header : {
            text : sensorDayReading.channel2Name,
            value : sensorDayReading.serial + '.ch2',
        }
    };

    if ( hasChannel2 )
        return [ channel1Header, channel2Header ];

    return [ channel1Header ];
}

export default {
    name: 'MainView',

    async created() {
        this.globalState = this.$root.$data.globalState;
        Hub.listen( 'monitor', this.hubListenCallback );
    },

    mounted() {
        this.showAccount();
    },

    destroyed() {
        Hub.remove( 'monitor', this.hubListenCallback );
    },

    data: () => ({

        globalState : {},

        currentDate : dayjs(),

        accountTitle : '',

        headers : [],
        readings : [],

        accountDevicesCache : {
            loginId : '',
            devices : [],
        },
    }),

    computed : {
        localDate() {
            return this.globalState.localDate();
        },
    },

    watch: {
    },

    methods : {
        hubListenCallback( data ) {
            if ( ! ['ev:setAccount', 'ev:changeDate'].includes( data.payload.event ) )
                return;

            this.showAccount();
        },

        showToday() {
            this.globalState.setDate( dayjs() );
        },

        showPreviousDate() {
            this.globalState.changeDate( -1 );
        },

        showNextDate() {
            this.globalState.changeDate( 1 );
        },

        showAccount() {
            const account = this.globalState.account;
            const localDate = this.globalState.localDate();

            // console.log( `Showing account ${account.description} (${account.loginId}) at ${localDate}` );

            if ( account ) 
                this.accountTitle = account.location + ': ' + account.description;
            else 
                this.accountTitle = '';

            this.getSensorDayReadings( account, localDate )
                .then( sensorDayReadings => this.makeTableData( account, sensorDayReadings.sort( compareSensorsByName ) ) )
                // .then( peek( 'table data' ) )
                .then( tableData => {
                    this.headers = tableData.headers;
                    this.readings = tableData.readings;
                } )
            ;
        },

        getAccountDevices( account ) {
            return pingAccount( this.globalState.accountDevicesCache, account );
        },

        async makeTableData( account, sensorDayReadings ) {

            const roundMinutes = date => date.minute( 10 * Math.round( date.minute() / 10 ) );

            const reduceSensorReadingByLocalDate = ( accumulator, sensorDayReading ) => {

                const reduceReadings = ( sensorKey ) => ( accumulator, reading ) => {
                    const localDate = dayjs.unix( reading.unixtime );
                    const rowMinute = localDate.format( 'mm' );
                    const rowTime = localDate.format( 'HH' ) + ':' + rowMinute.charAt( 0 ) + '0';
                    const sortRowTime = localDate.format( 'DD-HH' ) + ':' + rowMinute.charAt( 0 ) + '0';

                    if ( ! accumulator[ sortRowTime ] ) {
                        accumulator[sortRowTime] = { rowTime };
                    }

                    accumulator[ sortRowTime ].Fecha = roundMinutes( localDate ).second( 0 ).format( 'YYYY-MM-DD HH:mm:ss' );
                    accumulator[ sortRowTime ][ sensorKey ] = reading.reading + '°' ;

                    return accumulator; // [ { rowTime, Fecha, sensorKey } ]
                };

                sensorDayReading.readings.reduce( reduceReadings( sensorDayReading.serial ) , accumulator );
                const hasChannel2 = sensorDayReading.channel2Name != null;
                if ( hasChannel2 )
                    sensorDayReading.channel2Readings.reduce( reduceReadings( sensorDayReading.serial + '.ch2' ) , accumulator );

                return accumulator;
            };

            const readingsByLocalDate = Object.entries( sensorDayReadings.reduce( reduceSensorReadingByLocalDate, {} ) )
                                              .map( entry => entry[1] )
                                              .sort( ( left, right ) => left.sortRowTime < right.sortRowTime ? -1 : 1 );

            const dayReadingsSerials = sensorDayReadings.map( sensorDayReading => sensorDayReading.serial );
            const devices = await this.getAccountDevices( account );
            // await pingAccount( account ).catch( error => {
            // console.log( 'Error looking up account devices, will not pad table with empty datasets', error );
            // return [];
            //             } );

            const sensorHeaders = sensorDayReadings.flatMap( createSensorDayReadingHeaders );

            const emptyHeaders = devices
                .filter( device => ! dayReadingsSerials.includes( device.serial ) )
                .flatMap( device => {
                    const hasChannel2 = device.channel.length > 1;

                    const channel1Header = {
                        name : device.name + ( hasChannel2 ? ' ' + device.channel[0].name : '' ),
                        header : {
                            text : device.name + ( hasChannel2 ? ' ' + device.channel[0].name : '' ),
                            value : device.serial,
                        }
                    };
                    const channel2Header = ! hasChannel2 ? {} : {
                        name : device.name + device.channel[1].name,
                        header : {
                            text : device.name + ' ' + device.channel[1].name,
                            value : device.serial + '.ch2',
                        }
                    };

                    if ( hasChannel2 )
                        return [ channel1Header, channel2Header ];

                    return [ channel1Header ];
                } );

            const headers = sensorHeaders
                .concat( emptyHeaders )
                .sort( compareSensorsByName )
                .map( headerWrapper => headerWrapper.header );

            return {
                headers : [ { text : '', value : 'rowTime' }, ... headers ],
                readings : readingsByLocalDate,
            };
        },

        ////////////////////////////////////////////////////////////////////////////////
        // Back end

        getSensorDayReadings( account, localDate ) {
            if ( ! account )
                return Promise.resolve( [] );

            const params = {
                query : listSensorDayReadingsByAccountByDate,
                variables : {
                    tndAccountLoginId : account.loginId,
                    localDate : { eq : localDate },
                    limit : 1000,
                },
            };

            return API.graphql( params )
                      .then( result => result.data.listSensorDayReadingsByAccountByDate.items )
                      .catch( error => {
                          console.log( 'Error calling listSensorDayReadingsByAccountByDate(): ', error );
                          return [];
                      } );
        },

        ////////////////////////////////////////////////////////////////////////////////
        // Download

        downloadCsv() {
            if ( this.readings.length < 1 )
                return true;

            try {
                const account = this.globalState.account;
                const filename = account.location + '-' +
                                 account.description + '-' +
                                 this.globalState.localDate() + '.csv';

                const fields = this.headers.map( header => header.value === 'rowTime' ? 'Fecha' : header.text );

                const data = this.readings.map( reading => 
                    this.headers.map( header =>
                        header.value === 'rowTime' ? reading.Fecha :
                        reading[header.value] ? reading[header.value].slice( 0, -1 ) :
                        '' ) );

                const csv = Papa.unparse( { fields, data }, {
                    quotes : false,
                    quoteChar : '"',
                    escapeChar : '"',
                    delimiter : ',',
                    header : true,
                    newline : '\r\n',
                } );
                // utf-8 is not loaded properly by Excel
                // const uri = 'data:text/csv;charset=utf-8,' + '\uFEFF' + encodeURIComponent( csv );
                const uri = 'data:text/csv;base64,' + '77u/' + btoa( csv );

                const link = document.createElement( 'a' );
                link.id = 'csv-1';
                link.href = uri;

                document.body.appendChild( link );

                link.style.visibility = 'hidden';
                link.download = filename;

                link.click();

                setTimeout( () => document.body.removeChild( link ) );

                return true;
            }
            catch ( error ) {
                console.log( 'Error preparing CSV download link', error );

                return false;
            }
        },
    },
}
</script>

<style scoped>
</style>
